import styled from 'styled-components';
import { motion } from 'motion/react';
import { media } from '@styles/breakpoints';

/* ── Hero ─────────────────────────────────────────────────── */

export const HeroSection = styled.section`
  position: relative;
  min-height: 70vh;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.navy[950]};

  ${media.tablet} {
    min-height: 80vh;
  }
`;

export const HeroBg = styled(motion.div)<{ $image?: string; $color: string }>`
  position: absolute;
  top: -10%;
  left: -5%;
  right: -5%;
  bottom: -10%;
  z-index: 0;
  background-image: ${({ $image, $color }) => ($image ? `url(${$image})` : $color)};
  background-size: cover;
  background-position: center;
`;

export const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    to top,
    rgba(6, 18, 28, 0.98) 0%,
    rgba(6, 18, 28, 0.7) 40%,
    rgba(6, 18, 28, 0.3) 100%
  );
`;

export const HeroGrid = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background-image: radial-gradient(
    circle at 1px 1px,
    rgba(255, 255, 255, 0.04) 1px,
    transparent 0
  );
  background-size: 40px 40px;
  pointer-events: none;
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  padding-bottom: ${({ theme }) => theme.spacing[16]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

export const HeroBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

export const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.radii.full};
  background-color: rgba(0, 168, 240, 0.18);
  border: 1px solid rgba(0, 168, 240, 0.35);
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.cyan[300]};
`;

export const HeroStatusBadge = styled(HeroBadge)<{ $status: string }>`
  background-color: ${({ $status }) =>
    $status === 'upcoming'
      ? 'rgba(22, 163, 74, 0.18)'
      : $status === 'ongoing'
        ? 'rgba(0, 168, 240, 0.18)'
        : 'rgba(255,255,255,0.08)'};
  border-color: ${({ $status }) =>
    $status === 'upcoming'
      ? 'rgba(22, 163, 74, 0.4)'
      : $status === 'ongoing'
        ? 'rgba(0, 168, 240, 0.4)'
        : 'rgba(255,255,255,0.15)'};
  color: ${({ $status }) =>
    $status === 'upcoming'
      ? '#4ade80'
      : $status === 'ongoing'
        ? '#00A8F0'
        : 'rgba(255,255,255,0.5)'};
`;

export const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw + 1rem, 5rem);
  font-weight: ${({ theme }) => theme.fontWeights.extrabold};
  line-height: ${({ theme }) => theme.lineHeights.tight};
  letter-spacing: ${({ theme }) => theme.letterSpacings.tighter};
  color: white;
  max-width: 20ch;
`;

export const HeroTagline = styled(motion.p)`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: rgba(255, 255, 255, 0.65);
  max-width: 52ch;
`;

/* ── Info bar ─────────────────────────────────────────────── */

export const InfoBar = styled.div`
  background-color: ${({ theme }) => theme.colors.navy[800]};
  border-bottom: 1px solid ${({ theme }) => theme.semantic.border.subtle};
`;

export const InfoBarInner = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[6]};
  padding-block: ${({ theme }) => theme.spacing[5]};
`;

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

export const InfoLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
`;

export const InfoValue = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: rgba(255, 255, 255, 0.85);
`;

/* ── Conteúdo ─────────────────────────────────────────────── */

export const ContentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[12]};

  ${media.laptop} {
    flex-direction: row;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[16]};
  }
`;

export const ContentMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

export const ContentSidebar = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};

  ${media.laptop} {
    width: 320px;
    flex-shrink: 0;
    position: sticky;
    top: calc(${({ theme }) => theme.layout.headerHeight} + ${({ theme }) => theme.spacing[6]});
  }
`;

export const DescriptionParagraph = styled(motion.p)`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  color: ${({ theme }) => theme.semantic.text.secondary};

  &:first-of-type {
    font-size: ${({ theme }) => theme.fontSizes.xl};
    color: ${({ theme }) => theme.semantic.text.primary};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }
`;

/* ── Sidebar card ─────────────────────────────────────────── */

export const SideCard = styled.div`
  border-radius: ${({ theme }) => theme.radii.xl};
  border: 1px solid ${({ theme }) => theme.semantic.border.subtle};
  overflow: hidden;
`;

export const SideCardHeader = styled.div<{ $gradient: string }>`
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ $gradient }) => $gradient};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const SideCardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: white;
`;

export const SideCardSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: rgba(255, 255, 255, 0.65);
`;

export const SideCardBody = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.semantic.background.secondary};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

export const SideCardButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

/* ── Tags ─────────────────────────────────────────────────── */

export const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

export const Tag = styled.span`
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  border-radius: ${({ theme }) => theme.radii.full};
  background-color: ${({ theme }) => theme.semantic.background.tertiary};
  border: 1px solid ${({ theme }) => theme.semantic.border.subtle};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.semantic.text.tertiary};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wide};
`;

/* ── Back link ────────────────────────────────────────────── */

export const BackLink = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.semantic.text.inverseMuted};
  cursor: pointer;
  transition: color 0.2s ease;
  width: fit-content;

  &:hover {
    color: ${({ theme }) => theme.semantic.brand.accent};
  }
`;

/* ── 404 not found ────────────────────────────────────────── */

export const NotFoundWrapper = styled.div`
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]};
`;
